import { Fragment, lazy, Suspense } from 'react';
import { createRoutesFromElements, generatePath, Navigate, Route } from 'react-router';
import { observer } from 'mobx-react';
import debounce from 'lodash/debounce';
import { useMount, useUnmount } from '@cutwise/react-utils';

import { BILLING_PAGE } from '@/billing/constants/pages';
import { EDIT_MODE } from '@/collection/constants/pageModes';
import AppRoutes from '@/common/components/AppRoutes/AppRoutes';
import HistoryRouter from '@/common/components/HistoryRouter/HistoryRouter';
import Loader from '@/common/components/Loader/Loader';
import useModalScrollLock from '@/common/components/Modal/useModalScrollLock';
import RouteParams from '@/common/components/RouteParams/RouteParams';
import { HeaderSection } from '@/common/constants/HeaderSection';
import {
  ASET_EDUCATION_PAGE,
  ASET_PAGE,
  AUTH_PAGE,
  BRILLIANT_STORY_DEMO_PAGE,
  CARBON_VIEWER_PAGE,
  CATALOG_DIAMONDS_COLORLESS_EDIT_PAGE,
  CATALOG_DIAMONDS_COLORLESS_PAGE,
  CATALOG_DIAMONDS_FANCY_EDIT_PAGE,
  CATALOG_DIAMONDS_FANCY_PAGE,
  CATALOG_GEMS_EDIT_PAGE,
  CATALOG_GEMS_PAGE,
  CATALOG_JEWELRY_EDIT_PAGE,
  CATALOG_JEWELRY_PAGE,
  CATALOG_LGD_COLORLESS_EDIT_PAGE,
  CATALOG_LGD_COLORLESS_PAGE,
  CATALOG_LGD_FANCY_EDIT_PAGE,
  CATALOG_LGD_FANCY_PAGE,
  CATALOG_LGD_ROUGH_EDIT_PAGE,
  CATALOG_LGD_ROUGH_PAGE,
  CATALOG_ROUGH_EDIT_PAGE,
  CATALOG_ROUGH_PAGE,
  COLLECTIONS_PAGE,
  COMPARE_BY_PRODUCT_TYPE_PAGE,
  COMPARE_WITH_REFERENCE_PAGE,
  CONTACTS_PAGE,
  CROWD_SURVEY_PAGE,
  DEAD_ZONE_INSPECTOR_EDUCATION_PAGE,
  DEAD_ZONE_INSPECTOR_PAGE,
  DEAD_ZONE_INSPECTOR_PAGE_OLD,
  DIAMOND_DETAILS_EDIT_PAGE,
  DIAMOND_DETAILS_PAGE,
  DRIVE_PAGE,
  FANCY_COLOR_GRADING_PAGE,
  FCRF_DEMO_PAGE,
  GEM_DETAILS_PAGE,
  GEM_EDIT_DETAILS_PAGE,
  GRADING_PAGE,
  HOME_PAGE,
  JEWELRY_DETAILS_PAGE,
  JEWELRY_EDIT_DETAILS_PAGE,
  LOGIN_REDIRECT_PAGE,
  MULTI_PROJECT_HISTORY_PAGE,
  MULTI_PROJECTS_PAGE,
  OLD_GRADING_REDIRECT_PAGE,
  PRESENTATION_COLLECTIONS_PAGE,
  PROJECTS_PAGE,
  ROUGH_DETAILS_PAGE,
  ROUGH_EDIT_DETAILS_PAGE,
  SAME_LAB_PROPORTIONS_DEMO_PAGE,
  SHARED_MULTI_PROJECTS_PAGE,
  SHARED_WITH_ME_PROJECTS_PAGE,
  SINGLE_COLLECTION_EDIT_PAGE,
  SINGLE_COLLECTION_PAGE,
  SOLUTIONS_PAGE,
  SPECTRUM_PAGE,
  STEREO_PLAYER_PAGE,
  SURVEY_PAGE,
  SURVEY_RESULT_PAGE,
  SWEET_LINE_PAGE,
  SWEET_LINES_DEMO_PAGE,
  TASKS_PAGE,
  UI_KIT_PAGE,
  UPLOAD_IMAGE_PAGE,
  WEB_PLOTTING_DEMO_PAGE,
  WIDGET_CONSTRUCTOR_PAGE,
} from '@/common/constants/pages';
import { getEnvironment } from '@/common/helpers/getEnvironment';
import scrollTo from '@/common/helpers/scrollTo';
import App from '@/common/pages/App/App';
import Auth from '@/common/pages/Auth/Auth';
import RedirectPage from '@/common/pages/RedirectPage/RedirectPage';
import browserHistory from '@/common/services/history';
import Notifier from '@/common/services/Notifier';
import Storage, { KEY_AUTH_REDIRECT_STARTED, KEY_E2E } from '@/common/services/Storage';
import AppError from '@/common/state/AppError';
import { useAppStore } from '@/common/stores/AppStore';
import { CONVERSION_PAGE, SERVICES_PAGE } from '@/conversion/constants/pages';
import usePreloadDriveTree from '@/drive/hooks/usePreloadDriveTree';
import { parseDriveParams } from '@/drive/types/DriveSection';
import {
  APP_CONTEXT_PRODUCT_TYPES,
  COMPARE_WITH_REFERENCE_PRODUCT_TYPES,
  ProductType,
  TYPE_DIAMOND_COLORLESS,
  TYPE_DIAMOND_FANCY,
  TYPE_JEWELRY,
  TYPE_LGD_COLORLESS,
  TYPE_LGD_FANCY,
  TYPE_LGD_ROUGH,
  TYPE_ROUGH,
} from '@/product/constants/productTypes';
import { usePendingSpectrumTasksStore } from '@/spectrum/stores/PendingSpectrumTasksStore';

const LoginRedirect = lazy(() => import('@/common/pages/LoginRedirect/LoginRedirect'));
const Comparison = lazy(() => import('@/comparison/pages/Comparison/Comparison'));
const ReferenceComparison = lazy(() => import('@/comparison/pages/ReferenceComparison/ReferenceComparison'));
const ProductCatalog = lazy(() => import('@/catalog/pages/ProductCatalog/ProductCatalog'));
const ProjectsPage = lazy(() => import('@/hpo/pages/ProjectsPage/ProjectsPage'));
const SolutionsPage = lazy(() => import('@/hpo/pages/SolutionsPage/SolutionsPage'));
const SingleCVPage = lazy(() => import('@/hpo/pages/SingleCVPage/SingleCVPage'));
const SharedProjectsPage = lazy(() => import('@/hpo/pages/SharedProjectsPage/SharedProjectsPage'));
const StockManagementPage = lazy(() => import('@/catalog/pages/StockManagementPage/StockManagementPage'));
const DiamondDetails = lazy(() => import('@/product/pages/DiamondDetails/DiamondDetails'));
const DiamondEdit = lazy(() => import('@/product/pages/DiamondEdit/DiamondEdit'));
const RoughDetails = lazy(() => import('@/product/pages/RoughDetails/RoughDetails'));
const RoughEdit = lazy(() => import('@/product/pages/RoughEdit/RoughEdit'));
const JewelryDetails = lazy(() => import('@/product/pages/JewelryDetails/JewelryDetails'));
const GemDetails = lazy(() => import('@/product/pages/GemDetails/GemDetails'));
const GemEdit = lazy(() => import('@/product/pages/GemEdit/GemEdit'));
const JewelryEdit = lazy(() => import('@/product/pages/JewelryEdit/JewelryEdit'));
const CollectionRoute = lazy(() => import('@/collection/pages/CollectionRoute/CollectionRoute'));
const SweetLinePage = lazy(() => import('@/sweetLine/pages/SweetLinePage/SweetLinePage'));
const DiamondCollectionsPage = lazy(() => import('@/static/pages/DiamondCollectionsPage/DiamondCollectionsPage'));
const SameLabProportionsDemoPage = lazy(
  () => import('@/static/pages/SameLabProportionsDemoPage/SameLabProportionsDemoPage'),
);
const SweetLinesDemoPage = lazy(() => import('@/static/pages/SweetLinesDemoPage/SweetLinesDemoPage'));
const BrilliantStoryDemoPage = lazy(() => import('@/static/pages/BrilliantStoryDemoPage/BrilliantStoryDemoPage'));
const FCRFDemoPage = lazy(() => import('@/static/pages/FCRFDemoPage/FCRFDemoPage'));
const WebPlottingDemoPage = lazy(() => import('@/static/pages/WebPlottingDemoPage/WebPlottingDemoPage'));

const SurveyPage = lazy(() => import('@/survey/pages/SurveyPage/SurveyPage'));
const SurveyComparePage = lazy(() => import('@/survey/pages/SurveyComparePage/SurveyComparePage'));
const PresentationCollectionsPage = lazy(
  () => import('@/static/pages/PresentationCollectionsPage/PresentationCollectionsPage'),
);
const ErrorPage = lazy(() => import('@/common/pages/ErrorPage/ErrorPage'));
const MyProfilePage = lazy(() => import('@/user/pages/MyProfilePage/MyProfilePage'));
const PrivacyPolicy = lazy(() => import('@/common/pages/PrivacyPolicy/PrivacyPolicy'));
const UiKit = lazy(() => import('@/common/pages/UiKit/UiKit'));
const UploadImagePage = lazy(() => import('@/common/pages/UploadImagePage/UploadImagePage'));
const CustomGrading = lazy(() => import('@/customGrading/pages/CustomGrading/CustomGrading'));
const FCRFGradingPage = lazy(() => import('@/fcrf/pages/FCRFGradingPage/FCRFGradingPage'));
const Billing = lazy(() => import('@/billing/pages/Billing/Billing'));
const DrivePage = lazy(() => import('@/drive/pages/DrivePage/DrivePage'));
const DriveProjectsPage = lazy(() => import('@/multistage/pages/DriveProjectsPage/DriveProjectsPage'));
const DriveProjectHistoryPage = lazy(
  () => import('@/multistage/pages/DriveProjectHistoryPage/DriveProjectHistoryPage'),
);
const Services = lazy(() => import('@/conversion/pages/ServicesPage/ServicesPage'));
const Spectrum = lazy(() => import('@/spectrum/pages/SpectrumPage/SpectrumPage'));
const DeadZonesInspectorPage = lazy(() => import('@/deadZones/pages/DeadZonesInspectorPage/DeadZonesInspectorPage'));
const TasksPage = lazy(() => import('@/tasks/pages/TasksPage/TasksPage'));

const StereoPlayer = lazy(() => import('@/stereo/pages/StereoPlayer/StereoPlayer'));
const WidgetConstructorPage = lazy(() => import('@/widget/pages/WidgetConstructorPage/WidgetConstructorPage'));
const ContactsPage = lazy(() => import('@/security/pages/ContactsPage/ContactsPage'));

function Root() {
  const appStore = useAppStore();
  const pendingSpectrumTasksStore = usePendingSpectrumTasksStore();

  useModalScrollLock();
  usePreloadDriveTree();

  const setScreenWidth = debounce(
    () => {
      if (document.fullscreenElement && document.fullscreenEnabled) {
        return;
      }

      appStore.setScreenWidth(window.innerWidth);
      appStore.setScreenHeight(window.innerHeight);
    },
    100,
    { leading: true },
  );

  const setScreenOrientation = debounce(
    (e) => {
      if (!e.target?.type) {
        return;
      }

      appStore.setScreenOrientation(e.target.type);
    },
    100,
    { leading: true },
  );

  useMount(() => {
    browserHistory.listen(() => {
      Notifier.dismissAll();
      requestAnimationFrame(() => scrollTo(0, 0));
    });

    appStore.setScreenWidth(window.innerWidth);
    appStore.setScreenHeight(window.innerHeight);
    window.addEventListener('resize', setScreenWidth);
    screen.orientation.addEventListener('change', setScreenOrientation);
    pendingSpectrumTasksStore.listen();

    Storage.removeObject(KEY_AUTH_REDIRECT_STARTED, Storage.STORAGE_SESSION);
  });

  useUnmount(() => {
    window.removeEventListener('resize', setScreenWidth);
    screen.orientation.removeEventListener('change', setScreenOrientation);
    pendingSpectrumTasksStore.ignore();
  });

  const routes = createRoutesFromElements(
    <Fragment>
      {/* Public routes without wrapper, e.g. Header, etc */}

      <Route path={`${CARBON_VIEWER_PAGE}/:projectId/*`} element={<SingleCVPage />} />

      <Route path={`${AUTH_PAGE}/*`} element={<Auth />} />

      <Route path={`${LOGIN_REDIRECT_PAGE}/*`} element={<LoginRedirect />} />

      <Route path={`${STEREO_PLAYER_PAGE}/*`} element={<StereoPlayer />} />

      <Route path={FANCY_COLOR_GRADING_PAGE} element={<FCRFGradingPage />} />

      {/* Redirects below are for backward capability, do not delete them until it is necessary */}

      <Route path="/about/*" element={<RedirectPage to="https://octonus-teams.com/wiki/x/24WeAw" />} />

      <Route path="/diamonds/*" element={<RedirectPage to="https://octonus-teams.com/wiki/x/3oWeAw" />} />

      <Route path="/support/*" element={<RedirectPage to="https://octonus-teams.com/wiki/x/7A6UAw" />} />

      <Route path="/beauty/*" element={<RedirectPage to="https://octonus-teams.com/wiki/x/bg24Aw" />} />

      <Route path={HOME_PAGE} element={<Navigate replace to={CATALOG_DIAMONDS_COLORLESS_PAGE} />} />

      <Route path="/catalog" element={<Navigate replace to={CATALOG_DIAMONDS_COLORLESS_PAGE} />} />

      <Route path={`${CONVERSION_PAGE}/*`} element={<Navigate replace to={SERVICES_PAGE} />} />

      <Route
        path={`${ASET_PAGE}/*`}
        element={<Navigate replace to={`${DEAD_ZONE_INSPECTOR_PAGE}${location.search}`} />}
      />

      <Route
        path={`${ASET_EDUCATION_PAGE}/*`}
        element={<Navigate replace to={`${DEAD_ZONE_INSPECTOR_PAGE}${location.search}`} />}
      />

      <Route
        path={`${DEAD_ZONE_INSPECTOR_EDUCATION_PAGE}/*`}
        element={<Navigate replace to={`${DEAD_ZONE_INSPECTOR_PAGE}${location.search}`} />}
      />

      <Route
        path={`${DEAD_ZONE_INSPECTOR_PAGE_OLD}/*`}
        element={<Navigate replace to={`${DEAD_ZONE_INSPECTOR_PAGE}${location.search}`} />}
      />

      <Route
        path="/e2e/*"
        element={(
          <RouteParams>
            {() => {
              Storage.saveObject(KEY_E2E, true, Storage.STORAGE_LOCAL);

              return <Navigate replace to="/" />;
            }}
          </RouteParams>
        )}
      />

      {/* Routes with Header */}

      <Route element={<App />}>
        <Route
          path="/grading/r/:referenceCollectionId/c/:collectionToGradeId/:gradingFieldKey"
          element={<Navigate replace to={OLD_GRADING_REDIRECT_PAGE} />}
        />

        <Route
          path="/grading/r/:referenceCollectionId/c/:collectionToGradeId"
          element={<Navigate replace to={OLD_GRADING_REDIRECT_PAGE} />}
        />

        <Route
          path="/grading/by-collection/:referenceCollectionId/:id"
          element={<Navigate replace to={OLD_GRADING_REDIRECT_PAGE} />}
        />

        <Route
          path={DRIVE_PAGE}
          element={(
            <RouteParams>
              {(params) => <DrivePage params={parseDriveParams(params)} />}
            </RouteParams>
          )}
        />

        <Route
          path={`${TASKS_PAGE}/*`}
          element={<TasksPage />}
        />

        <Route
          path={CONTACTS_PAGE}
          element={<ContactsPage />}
        />

        <Route
          path={MULTI_PROJECTS_PAGE}
          element={<DriveProjectsPage key="owned" />}
        />

        <Route
          path={SHARED_MULTI_PROJECTS_PAGE}
          element={<DriveProjectsPage key="shared" isShared />}
        />

        <Route
          path={MULTI_PROJECT_HISTORY_PAGE}
          element={<DriveProjectHistoryPage />}
        />

        <Route
          path={`${SURVEY_RESULT_PAGE}/:collectionId/:surveyId`}
          element={<SurveyComparePage />}
        />

        <Route
          path={`${PRESENTATION_COLLECTIONS_PAGE}/*`}
          element={<PresentationCollectionsPage />}
        />

        <Route
          path={PROJECTS_PAGE}
          element={<ProjectsPage />}
        />

        <Route
          path={SHARED_WITH_ME_PROJECTS_PAGE}
          element={<SharedProjectsPage />}
        />

        <Route
          path="/profile/*"
          element={<MyProfilePage />}
        />

        <Route
          path={`${BILLING_PAGE}/*`}
          element={<Billing />}
        />

        <Route
          path={`${SERVICES_PAGE}/*`}
          element={<Services />}
        />

        <Route
          path={`${WIDGET_CONSTRUCTOR_PAGE}/*`}
          element={(
            <RouteParams>{(params) => <WidgetConstructorPage productId={params.productId} />}</RouteParams>
          )}
        />

        <Route
          path="/privacy/*"
          element={<PrivacyPolicy />}
        />

        {getEnvironment() !== 'production' && (
          <Route
            path={`${UPLOAD_IMAGE_PAGE}/*`}
            element={<UploadImagePage />}
          />
        )}

        {getEnvironment() !== 'production' && (
          <Route
            path={`${UI_KIT_PAGE}/*`}
            element={<UiKit />}
          />
        )}

        <Route
          path="*"
          element={<ErrorPage error={AppError.create404Error()} />}
        />

        <Route handle={{ headerSection: HeaderSection.NaturalDiamonds }}>
          <Route
            path={CATALOG_ROUGH_EDIT_PAGE}
            element={<StockManagementPage productType={TYPE_ROUGH} />}
          />

          <Route
            path={CATALOG_DIAMONDS_FANCY_EDIT_PAGE}
            element={<StockManagementPage productType={TYPE_DIAMOND_FANCY} />}
          />

          <Route
            path={CATALOG_DIAMONDS_COLORLESS_EDIT_PAGE}
            element={<StockManagementPage productType={TYPE_DIAMOND_COLORLESS} />}
          />

          <Route
            path={CATALOG_ROUGH_PAGE}
            element={<ProductCatalog productType={TYPE_ROUGH} />}
          />

          <Route
            path={CATALOG_DIAMONDS_FANCY_PAGE}
            element={<ProductCatalog productType={TYPE_DIAMOND_FANCY} />}
          />

          <Route
            path={CATALOG_DIAMONDS_COLORLESS_PAGE}
            element={<ProductCatalog productType={TYPE_DIAMOND_COLORLESS} />}
          />
        </Route>

        <Route handle={{ headerSection: HeaderSection.LabGrownDiamonds }}>
          <Route
            path={CATALOG_LGD_FANCY_EDIT_PAGE}
            element={<StockManagementPage productType={TYPE_LGD_FANCY} />}
          />

          <Route
            path={CATALOG_LGD_COLORLESS_EDIT_PAGE}
            element={<StockManagementPage productType={TYPE_LGD_COLORLESS} />}
          />

          <Route
            path={CATALOG_LGD_ROUGH_EDIT_PAGE}
            element={<StockManagementPage productType={TYPE_LGD_ROUGH} />}
          />

          <Route
            path={CATALOG_LGD_FANCY_PAGE}
            element={<ProductCatalog productType={TYPE_LGD_FANCY} />}
          />

          <Route
            path={CATALOG_LGD_COLORLESS_PAGE}
            element={<ProductCatalog productType={TYPE_LGD_COLORLESS} />}
          />

          <Route
            path={CATALOG_LGD_ROUGH_PAGE}
            element={<ProductCatalog productType={TYPE_LGD_ROUGH} />}
          />
        </Route>

        <Route handle={{ headerSection: HeaderSection.Jewelry }}>
          <Route
            path={CATALOG_JEWELRY_EDIT_PAGE}
            element={<StockManagementPage productType={TYPE_JEWELRY} />}
          />

          <Route
            path={CATALOG_JEWELRY_PAGE}
            element={<ProductCatalog productType={TYPE_JEWELRY} />}
          />

          <Route
            path={JEWELRY_EDIT_DETAILS_PAGE}
            element={(
              <RouteParams>
                {(params) => <JewelryEdit routeParams={{ id: params.id! }} />}
              </RouteParams>
            )}
          />

          <Route
            path={JEWELRY_DETAILS_PAGE}
            element={(
              <RouteParams>
                {(params) => <JewelryDetails routeParams={{ id: params.id! }} />}
              </RouteParams>
            )}
          />
        </Route>

        <Route handle={{ headerSection: HeaderSection.Gems }}>
          <Route
            path={CATALOG_GEMS_PAGE}
            element={<ProductCatalog productType={ProductType.Gem} />}
          />

          <Route
            path={CATALOG_GEMS_EDIT_PAGE}
            element={<StockManagementPage productType={ProductType.Gem} />}
          />

          <Route
            path={GEM_DETAILS_PAGE}
            element={(
              <RouteParams>
                {(params) => <GemDetails search={location.search} id={params.id!} />}
              </RouteParams>
            )}
          />

          <Route
            path={GEM_EDIT_DETAILS_PAGE}
            element={(
              <RouteParams>
                {(params) => <GemEdit id={params.id!} />}
              </RouteParams>
            )}
          />
        </Route>

        <Route handle={{ headerSection: HeaderSection.Demo }}>
          <Route
            path={SWEET_LINE_PAGE}
            element={(
              <RouteParams>
                {(params) => <SweetLinePage collectionId={params.collectionId!} />}
              </RouteParams>
            )}
          />

          <Route
            path={`${COLLECTIONS_PAGE}/*`}
            element={<DiamondCollectionsPage />}
          />

          <Route
            path={SAME_LAB_PROPORTIONS_DEMO_PAGE}
            element={<SameLabProportionsDemoPage />}
          />

          <Route
            path={`${SPECTRUM_PAGE}/*`}
            element={<Spectrum />}
          />

          <Route
            path={`${DEAD_ZONE_INSPECTOR_PAGE}/*`}
            element={<DeadZonesInspectorPage />}
          />

          <Route
            path={SWEET_LINES_DEMO_PAGE}
            element={<SweetLinesDemoPage />}
          />

          <Route
            path={BRILLIANT_STORY_DEMO_PAGE}
            element={<BrilliantStoryDemoPage />}
          />

          <Route
            path={FCRF_DEMO_PAGE}
            element={<FCRFDemoPage />}
          />

          <Route
            path={WEB_PLOTTING_DEMO_PAGE}
            element={<WebPlottingDemoPage />}
          />
        </Route>

        <Route handle={{ headerSection: HeaderSection.Dynamic }}>
          {COMPARE_WITH_REFERENCE_PRODUCT_TYPES.map((productType) => (
            <Route
              key={productType}
              path={COMPARE_WITH_REFERENCE_PAGE.replace(':productType', productType)}
              element={(
                <RouteParams>
                  {(params) => <ReferenceComparison routeParams={{ productType, id: params.id! }} />}
                </RouteParams>
              )}
            />
          ))}

          {APP_CONTEXT_PRODUCT_TYPES.map((productType) => (
            <Route
              key={productType}
              path={generatePath(COMPARE_BY_PRODUCT_TYPE_PAGE, { productType })}
              element={<Comparison routeParams={{ productType }} />}
            />
          ))}

          <Route
            path={DIAMOND_DETAILS_EDIT_PAGE}
            element={(
              <RouteParams>
                {(params) => <DiamondEdit routeParams={{ id: params.id! }} />}
              </RouteParams>
            )}
          />

          <Route
            path={DIAMOND_DETAILS_PAGE}
            element={(
              <RouteParams>
                {(params) => <DiamondDetails routeParams={{ id: params.id! }} />}
              </RouteParams>
            )}
          />

          <Route
            path={ROUGH_EDIT_DETAILS_PAGE}
            element={(
              <RouteParams>
                {(params) => <RoughEdit routeParams={{ id: params.id! }} />}
              </RouteParams>
            )}
          />

          <Route
            path={ROUGH_DETAILS_PAGE}
            element={(
              <RouteParams>
                {(params) => <RoughDetails routeParams={{ id: params.id! }} />}
              </RouteParams>
            )}
          />

          <Route
            path={`${GRADING_PAGE}/:customGradeCode`}
            element={<CustomGrading />}
          />

          <Route
            path={SINGLE_COLLECTION_EDIT_PAGE}
            element={(
              <RouteParams>
                {(params) => <CollectionRoute mode={EDIT_MODE} slug={params.collectionSlug} />}
              </RouteParams>
            )}
          />

          <Route
            path={SINGLE_COLLECTION_PAGE}
            element={(
              <RouteParams>
                {(params) => <CollectionRoute slug={params.collectionSlug} />}
              </RouteParams>
            )}
          />

          <Route
            path={`${SURVEY_PAGE}/:surveyId`}
            element={<SurveyPage metaType="survey" />}
          />

          <Route
            path={`${CROWD_SURVEY_PAGE}/:surveyId`}
            element={<SurveyPage metaType="crowd-survey" />}
          />

          <Route
            path={SOLUTIONS_PAGE}
            element={<SolutionsPage />}
          />
        </Route>
      </Route>
    </Fragment>,
  );

  return (
    <HistoryRouter history={browserHistory}>
      <Suspense fallback={<Loader />}>
        <AppRoutes routes={routes} />
      </Suspense>
    </HistoryRouter>
  );
}

export default observer(Root);
